import { FC, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, ContentLabelField, ContentList, Divider, Grid, Text, Toggle } from '@hopskipdrive/react-components';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { colorTokens } from '@rideiq/shared/components/variables';

import { ListInfo } from './styles';

export interface PermissionsProps {
  accountInfo?: AccountInfo;
  editMode: boolean;
}

const Permissions: FC<PermissionsProps> = ({ accountInfo, editMode }) => {
  const { control } = useFormContext();
  const [permissions, setPermissions] = useState({
    cancelIndividualRides: accountInfo?.cancelIndividualRides,
    cancelAllRides: accountInfo?.cancelAllRides,
    changeTimeOfRides: accountInfo?.changeTimeOfRides,
    changePuOrDoLocations: accountInfo?.changePuOrDoLocations,
  });

  useEffect(() => {
    setPermissions({
      cancelIndividualRides: accountInfo?.cancelIndividualRides,
      cancelAllRides: accountInfo?.cancelAllRides,
      changeTimeOfRides: accountInfo?.changeTimeOfRides,
      changePuOrDoLocations: accountInfo?.changePuOrDoLocations,
    });
  }, [accountInfo]);

  return (
    <Box>
      <PageHeading variant="h2" fontWeight={700} my={4}>
        Permissions
      </PageHeading>
      <Grid container>
        <Grid item xs={6}>
          <ContentList mb={4}>
            <Text variant="h3" fontWeight={600}>
              {"What can Riders' Caregivers do if they contact us for support?"}
            </Text>
          </ContentList>
          <ContentLabelField
            id="cancelIndividualRides"
            title="Cancel Individual Rides"
            description={permissions.cancelIndividualRides}
            editMode={editMode}
            editView={
              <Controller
                render={({ field: { onChange } }) => (
                  <Toggle
                    value={permissions.cancelIndividualRides}
                    onChange={(_, v) => {
                      if (v) {
                        onChange(v);
                        setPermissions({ ...permissions, cancelIndividualRides: v });
                      }
                    }}
                    exclusive
                  >
                    <Toggle.Option value="Yes">Yes</Toggle.Option>
                    <Toggle.Option value="No">No</Toggle.Option>
                  </Toggle>
                )}
                name="cancelIndividualRides"
                control={control}
              />
            }
          />
          <ContentLabelField
            id="cancelAllRides"
            title="Cancel All Rides"
            description={permissions.cancelAllRides}
            editMode={editMode}
            editView={
              <Controller
                render={({ field: { onChange } }) => (
                  <Toggle
                    value={permissions.cancelAllRides}
                    onChange={(_, v) => {
                      if (v) {
                        onChange(v);
                        setPermissions({ ...permissions, cancelAllRides: v });
                      }
                    }}
                    exclusive
                  >
                    <Toggle.Option value="Yes">Yes</Toggle.Option>
                    <Toggle.Option value="No">No</Toggle.Option>
                  </Toggle>
                )}
                name="cancelAllRides"
                control={control}
              />
            }
          />
          <ContentLabelField
            id="changeTimeOfRides"
            title="Change Time of Rides"
            description={permissions.changeTimeOfRides}
            editMode={editMode}
            editView={
              <Controller
                render={({ field: { onChange } }) => (
                  <Toggle
                    value={permissions.changeTimeOfRides}
                    onChange={(_, v) => {
                      if (v) {
                        onChange(v);
                        setPermissions({ ...permissions, changeTimeOfRides: v });
                      }
                    }}
                    exclusive
                  >
                    <Toggle.Option value="Yes">Yes</Toggle.Option>
                    <Toggle.Option value="No">No</Toggle.Option>
                  </Toggle>
                )}
                name="changeTimeOfRides"
                control={control}
              />
            }
          />
          <ContentLabelField
            id="changePuOrDoLocations"
            title="Change Pickup or Dropoff Locations"
            description={permissions.changePuOrDoLocations}
            editMode={editMode}
            editView={
              <Controller
                render={({ field: { onChange } }) => (
                  <Toggle
                    value={permissions.changePuOrDoLocations}
                    onChange={(_, v) => {
                      if (v) {
                        onChange(v);
                        setPermissions({ ...permissions, changePuOrDoLocations: v });
                      }
                    }}
                    exclusive
                  >
                    <Toggle.Option value="Yes">Yes</Toggle.Option>
                    <Toggle.Option value="No">No</Toggle.Option>
                  </Toggle>
                )}
                name="changePuOrDoLocations"
                control={control}
              />
            }
          />
        </Grid>
        <Grid item xs={1} style={{ maxWidth: '40px' }}>
          <Divider orientation="vertical" style={{ width: '1px' }} />
        </Grid>
        <Grid item xs={3} md={4}>
          <Box>
            <Box bgcolor={colorTokens.background.light} pb={4}>
              <ContentList>
                <Text p={8} pb={0} fontWeight={600}>
                  {"We help you field questions from riders' caregivers. Tell us what they can do."}
                </Text>
              </ContentList>
              <ContentList>
                <ListInfo>
                  <li className={'orange'}>
                    <Text>Allowing caregivers to cancel rides can reduce last-minute cancellation charges</Text>
                  </li>
                </ListInfo>
              </ContentList>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Permissions;
